import { useState } from "react";
import { BookOpenCheck, ImagePlus, Send, Trash2 } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { useOrganization } from "../organization/useOrganization";
import { useScheduling } from "../scheduling/useScheduling";
import { Button } from "../../shared/ui/Button";
import { Card, EmptyState } from "../../shared/ui/Card";
import { PageSkeleton } from "../../shared/ui/Status";
import { useLearning, useLearningActions } from "./useLearning";

export function TeachingFeedbackPanel() {
  const { session } = useAuth();
  const org = useOrganization();
  const scheduling = useScheduling();
  const learning = useLearning();
  const actions = useLearningActions();
  const [selected, setSelected] = useState("");
  const [summary, setSummary] = useState("");
  const [strengths, setStrengths] = useState("");
  const [improvements, setImprovements] = useState("");
  const [homework, setHomework] = useState("");
  const [mediaFeedbackId, setMediaFeedbackId] = useState("");
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [caption, setCaption] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  if (!session || !org.data || !scheduling.data || !learning.data) return <PageSkeleton />;

  const lessons = scheduling.data.schedules.filter((item) => item.coachId === session.profile.id && item.status === "completed");
  const feedback = learning.data.feedback.filter((item) => item.coachId === session.profile.id);
  const tasks = learning.data.feedbackTasks.filter((item) => item.coachId === session.profile.id && item.status !== "published");
  const media = learning.data.growthMedia.filter((item) => item.coachId === session.profile.id);
  const names = Object.fromEntries(org.data.accounts.map((item) => [item.id, item.name]));
  const submit = async () => {
    setError(""); setNotice("");
    try {
      await actions.submitFeedback({ scheduleId: selected, summary, strengths, improvements, homework });
      setNotice("课后反馈已发布，会员现在可以查看并评分。");
      setSummary(""); setStrengths(""); setImprovements(""); setHomework("");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "发布失败"); }
  };
  const upload = async () => {
    setError(""); setNotice("");
    if (!mediaFile) return setError("请选择图片或视频。");
    try {
      await actions.uploadGrowthMedia({ feedbackId: mediaFeedbackId, file: mediaFile, caption });
      setNotice("成长影像已安全发布，仅授权角色和会员本人可见。");
      setMediaFile(null); setCaption("");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "上传失败"); }
  };
  const remove = async (mediaId: string) => {
    if (!window.confirm("确定删除该成长影像吗？删除后文件不可恢复。")) return;
    const reason = window.prompt("请输入删除原因（至少2个字）", "上传错误");
    if (!reason) return;
    try { await actions.deleteGrowthMedia({ mediaId, reason }); setNotice("成长影像已删除并记录审计日志。"); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "删除失败"); }
  };

  return <section className="learning-panel">
    <div className="section-divider"><span>教学服务闭环</span><h2>课后反馈与成长影像</h2></div>
    {tasks.length > 0 && <div className={tasks.some((item) => item.status === "overdue") ? "inline-notice inline-notice--error" : "inline-notice"}>待反馈 {tasks.length} 项，其中逾期 {tasks.filter((item) => item.status === "overdue").length} 项；反馈须在课消后24小时内完成。</div>}
    {(notice || error) && <div className={error ? "inline-notice inline-notice--error" : "inline-notice"}>{error || notice}</div>}
    {lessons.length ? <div className="management-grid">
      <Card className="management-card">
        <div className="card-heading"><BookOpenCheck /><div><span>完成课消后填写</span><h2>发布成长反馈</h2></div></div>
        <div className="v4-form">
          <label className="v4-field"><span>已完成课程</span><select value={selected} onChange={(event) => setSelected(event.target.value)}><option value="">请选择</option>{lessons.map((lesson) => <option value={lesson.id} key={lesson.id}>{names[lesson.memberId]} · {new Date(lesson.startAt).toLocaleString("zh-CN")}</option>)}</select></label>
          <label className="v4-field"><span>课程小结</span><textarea value={summary} onChange={(event) => setSummary(event.target.value)} placeholder="本节课完成了什么" /></label>
          <div className="form-split"><label className="v4-field"><span>表现亮点</span><textarea value={strengths} onChange={(event) => setStrengths(event.target.value)} placeholder="值得肯定的进步" /></label><label className="v4-field"><span>提升建议</span><textarea value={improvements} onChange={(event) => setImprovements(event.target.value)} placeholder="下一步训练重点" /></label></div>
          <label className="v4-field"><span>课后练习</span><input value={homework} onChange={(event) => setHomework(event.target.value)} placeholder="可选：建议完成的练习" /></label>
          <Button onClick={submit} disabled={actions.pending || !selected}><Send size={15} />发布给会员</Button>
        </div>
      </Card>
      <Card className="management-card">
        <div className="card-heading"><ImagePlus /><div><span>私有文件 · 最大50MB</span><h2>上传成长影像</h2></div></div>
        <div className="v4-form">
          <label className="v4-field"><span>关联课后反馈</span><select value={mediaFeedbackId} onChange={(event) => setMediaFeedbackId(event.target.value)}><option value="">请选择</option>{feedback.map((item) => <option key={item.id} value={item.id}>{names[item.memberId]} · {item.summary}</option>)}</select></label>
          <label className="v4-field"><span>图片或视频</span><input type="file" accept="image/*,video/*" onChange={(event) => setMediaFile(event.target.files?.[0] ?? null)} /></label>
          <label className="v4-field"><span>影像说明</span><input value={caption} onChange={(event) => setCaption(event.target.value)} maxLength={500} placeholder="记录动作进步或训练重点" /></label>
          <Button onClick={upload} disabled={actions.pending || !mediaFeedbackId || !mediaFile}><ImagePlus size={15} />安全上传</Button>
        </div>
        <div className="feedback-history">{media.slice(0, 4).map((item) => <article key={item.id}><div><strong>{names[item.memberId]} · {item.fileName}</strong><p>{item.caption || "训练成长影像"}</p></div><button className="icon-button" onClick={() => remove(item.id)} aria-label="删除成长影像"><Trash2 size={16} /></button></article>)}</div>
      </Card>
    </div> : <EmptyState title="暂无待反馈课程" description="完成签到和课消后，对应课程会进入课后反馈列表。" />}
  </section>;
}
